<!-- 自提门店核销会员多选（verifyUserIds 存会员 ID） -->
<template>
  <Dialog :title="dialogTitle" v-model="dialogVisible" width="60%">
    <ContentWrap>
      <el-form
        class="-mb-15px"
        :model="queryParams"
        ref="queryFormRef"
        :inline="true"
        label-width="68px"
      >
        <el-form-item label="昵称" prop="nickname">
          <el-input
            v-model="queryParams.nickname"
            placeholder="请输入会员昵称"
            clearable
            @keyup.enter="handleQuery"
            class="!w-200px"
          />
        </el-form-item>
        <el-form-item label="手机号" prop="mobile">
          <el-input
            v-model="queryParams.mobile"
            placeholder="请输入手机号"
            clearable
            @keyup.enter="handleQuery"
            class="!w-200px"
          />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-select
            v-model="queryParams.status"
            placeholder="用户状态"
            clearable
            class="!w-160px"
          >
            <el-option
              v-for="dict in getIntDictOptions(DICT_TYPE.COMMON_STATUS)"
              :key="dict.value"
              :label="dict.label"
              :value="dict.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button @click="handleQuery"><Icon icon="ep:search" />搜索</el-button>
          <el-button @click="resetQuery"><Icon icon="ep:refresh" />重置</el-button>
        </el-form-item>
      </el-form>
    </ContentWrap>
    <ContentWrap>
      <el-table v-loading="loading" :data="list">
        <el-table-column width="55">
          <template #header>
            <el-checkbox
              v-model="isCheckAll"
              :indeterminate="isIndeterminate"
              @change="handleCheckAll"
            />
          </template>
          <template #default="{ row }">
            <el-checkbox
              v-model="checkedStatus[row.id!]"
              @change="(checked: boolean) => handleCheckOne(checked, row, true)"
            />
          </template>
        </el-table-column>
        <el-table-column label="会员编号" align="center" key="id" prop="id" width="90" />
        <el-table-column
          label="昵称"
          align="center"
          prop="nickname"
          :show-overflow-tooltip="true"
        />
        <el-table-column label="手机号" align="center" prop="mobile" width="130" />
        <el-table-column label="状态" key="status" width="90">
          <template #default="scope">
            <dict-tag :type="DICT_TYPE.COMMON_STATUS" :value="scope.row.status" />
          </template>
        </el-table-column>
      </el-table>
      <Pagination
        :total="total"
        v-model:page="queryParams.pageNo"
        v-model:limit="queryParams.pageSize"
        @pagination="getList"
      />
    </ContentWrap>
    <template #footer>
      <el-button type="primary" @click="handleEmitChange">确 定</el-button>
      <el-button @click="dialogVisible = false">取 消</el-button>
    </template>
  </Dialog>
</template>
<script lang="ts" setup>
import { DICT_TYPE, getIntDictOptions } from '@/utils/dict'
import * as MemberUserApi from '@/api/member/user'
import type { UserVO } from '@/api/member/user'

const isCheckAll = ref(false)
const isIndeterminate = ref(false)
const checkedUsers = ref<UserVO[]>([])
const checkedStatus = ref<Record<string, boolean>>({})

const dialogTitle = '选择核销会员'
const dialogVisible = ref(false)
const loading = ref(true)
const total = ref(0)
const list = ref<UserVO[]>([])
const queryParams = reactive({
  pageNo: 1,
  pageSize: 10,
  nickname: undefined as string | undefined,
  mobile: undefined as string | undefined,
  status: undefined as number | undefined
})
const queryFormRef = ref()

const getList = async () => {
  loading.value = true
  try {
    const data = await MemberUserApi.getUserPage(queryParams)
    list.value = data.list
    total.value = data.total
    calculateIsCheckAll()
  } finally {
    loading.value = false
  }
}

const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

const resetQuery = () => {
  queryFormRef.value?.resetFields()
  handleQuery()
}

const open = async (selected?: UserVO[]) => {
  dialogVisible.value = true
  checkedUsers.value = selected ? [...selected] : []
  checkedStatus.value = {}
  checkedUsers.value.forEach((u) => {
    if (u.id != null) checkedStatus.value[u.id] = true
  })
  loading.value = true
  try {
    await getList()
  } finally {
    loading.value = false
  }
}
defineExpose({ open })

const handleCheckAll = (checked: boolean) => {
  isCheckAll.value = checked
  isIndeterminate.value = false
  list.value.forEach((user) => handleCheckOne(checked, user, false))
}

const handleCheckOne = (checked: boolean, user: UserVO, isCalcCheckAll: boolean) => {
  if (user.id == null) return
  if (checked) {
    if (findCheckedIndex(user) < 0) {
      checkedUsers.value.push(user)
    }
    checkedStatus.value[user.id] = true
  } else {
    const index = findCheckedIndex(user)
    if (index > -1) {
      checkedUsers.value.splice(index, 1)
      checkedStatus.value[user.id] = false
      isCheckAll.value = false
    }
  }
  if (isCalcCheckAll) {
    calculateIsCheckAll()
  }
}

const findCheckedIndex = (user: UserVO) =>
  checkedUsers.value.findIndex((item) => item.id === user.id)

const calculateIsCheckAll = () => {
  isCheckAll.value =
    list.value.length > 0 && list.value.every((user) => user.id != null && checkedStatus.value[user.id])
  isIndeterminate.value =
    !isCheckAll.value && list.value.some((user) => user.id != null && checkedStatus.value[user.id])
}

const handleEmitChange = () => {
  dialogVisible.value = false
  emits('change', [...checkedUsers.value])
}

const emits = defineEmits<{
  change: [users: UserVO[]]
}>()
</script>
